import * as THREE from 'three';

// Lanterns are GENERATED, not downloaded.
//
// A lantern is a few boxes and a glowing panel — modelling that as a GLB and
// shipping it through the CDN would cost a network round trip and a texture
// budget for something the GPU can build in a millisecond. Generating it also
// means the emissive material is ours to drive from the day/night cycle
// instead of baked into someone's export.
//
// The light is the interesting half. One PointLight per lantern is not an
// option: every additional light recompiles and slows every lit material in
// the scene, and a hundred lanterns would bring the frame rate down to a
// crawl regardless of how small each one is. So the geometry is instanced
// without limit and a small POOL of real lights follows the camera, assigned
// each frame to the nearest lanterns. Up close you get true illumination that
// spills onto walls and paving; further out the emissive panel alone carries
// the read, which is all the eye asks for at that distance.

/** Real lights in play at once. Past this the emissive panel does the work. */
const LIGHT_POOL_SIZE = 6;
/** Lights beyond this distance are not worth a pool slot. */
const LIGHT_RANGE_M = 60;
/** Lamp head height as a fraction of the lantern's total height. */
const HEAD_FRACTION = 0.82;

const GLOW_COLOR = new THREE.Color('#ffb457');

/**
 * One unit-height lantern, origin at the base, built from a shared geometry
 * set. The caller scales it to the size the layout asks for.
 */
export function buildLanternGroup(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'lantern';

    const metal = new THREE.MeshStandardMaterial({
        color: '#2a2622',
        roughness: 0.6,
        metalness: 0.8,
    });

    // Post: square section, tapering slightly, most of the height.
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.032, HEAD_FRACTION, 6), metal);
    post.position.y = HEAD_FRACTION / 2;
    group.add(post);

    // Base plate, so it sits on paving instead of growing out of it.
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.075, 0.035, 8), metal);
    base.position.y = 0.017;
    group.add(base);

    // Head: a lantern box with a glowing panel inside and a cap over it.
    const headY = HEAD_FRACTION + 0.075;
    const cage = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.15, 0.13), metal);
    cage.position.y = headY;
    group.add(cage);

    const cap = new THREE.Mesh(new THREE.ConeGeometry(0.115, 0.07, 4), metal);
    cap.position.y = headY + 0.105;
    cap.rotation.y = Math.PI / 4;
    group.add(cap);

    // The glow itself: unlit and emissive so it reads as a light source at any
    // exposure, and slightly proud of the cage so it is never z-fought.
    const glass = new THREE.Mesh(
        new THREE.BoxGeometry(0.105, 0.125, 0.105),
        new THREE.MeshStandardMaterial({
            color: GLOW_COLOR,
            emissive: GLOW_COLOR,
            emissiveIntensity: 2.4,
            roughness: 1,
            metalness: 0,
        }),
    );
    glass.position.y = headY;
    glass.name = 'lantern-glass';
    group.add(glass);

    return group;
}

export interface LanternLights {
    /** Re-assign the pool to the lanterns nearest the camera. */
    update(camera: THREE.Camera): void;
    /** 0 = full day (lights off), 1 = night (full brightness). */
    setNight(night: number): void;
    dispose(): void;
}

/**
 * A pool of PointLights that chases the nearest lantern positions.
 *
 * Positions arrive in WORLD units. The pool is fixed size, so the cost is
 * constant no matter how many lanterns the layout contains.
 */
export function createLanternLights(opts: {
    scene: THREE.Scene;
    unitsPerMeter: number;
}): LanternLights & { setPositions(positions: THREE.Vector3[]): void } {
    const { scene, unitsPerMeter } = opts;
    const group = new THREE.Group();
    group.name = 'lantern-lights';
    scene.add(group);

    const lights: THREE.PointLight[] = [];
    for (let i = 0; i < LIGHT_POOL_SIZE; i++) {
        const light = new THREE.PointLight(GLOW_COLOR, 0, LIGHT_RANGE_M * unitsPerMeter * 0.5, 2);
        light.visible = false;
        light.castShadow = false;
        group.add(light);
        lights.push(light);
    }

    let positions: THREE.Vector3[] = [];
    let night = 1;
    const ranked: { position: THREE.Vector3; distance: number }[] = [];

    const apply = (camera: THREE.Camera) => {
        ranked.length = 0;
        const maxDistance = LIGHT_RANGE_M * unitsPerMeter;
        for (const position of positions) {
            const distance = camera.position.distanceTo(position);
            if (distance <= maxDistance) ranked.push({ position, distance });
        }
        ranked.sort((a, b) => a.distance - b.distance);

        for (let i = 0; i < lights.length; i++) {
            const entry = ranked[i];
            const light = lights[i];
            if (!entry || night <= 0.01) {
                light.visible = false;
                continue;
            }
            light.visible = true;
            light.position.copy(entry.position);
            // Fade the outermost slot rather than popping it as the camera
            // moves, and scale the whole thing by nightfall.
            const fade = 1 - THREE.MathUtils.smoothstep(entry.distance / maxDistance, 0.7, 1);
            light.intensity = 26 * unitsPerMeter * unitsPerMeter * night * fade;
        }
    };

    return {
        setPositions(next) {
            positions = next;
        },
        update(camera) {
            apply(camera);
        },
        setNight(value) {
            night = THREE.MathUtils.clamp(value, 0, 1);
        },
        dispose() {
            scene.remove(group);
            for (const light of lights) light.dispose?.();
        },
    };
}
